import type { PropType } from 'vue';
import { ViewTypes } from '~/constants/ui.constants';
import { getFromState } from '~/composables';
import { currencyFormat } from '~/helpers';

import type { Component } from '~/types';
import { mapProperty } from '~/units/properties/store';
import type { SinglePropertyRepo, SingleProperty, PropertyItemComponent } from '~/units/properties/types';
import { FullScreenPopupMixin } from '~/units/core/mixins/fullScreenPopupMixin';
import { usePropertiesStore } from '~/units/properties/stores';
import { useUsersStore } from '~/units/users/store';
import { RepoStates } from '~/units/core/types';
import { mapActions, mapState } from 'pinia';
import { ROUTES } from '~/constants/ui.constants';

import { useDataFromState } from '~/composables';

export const PropertyMixin = defineNuxtComponent({
  props: {
    component: {
      required: true,
      type: Object as PropType<Component>,
    },
    propertyIdList: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    preMarketBlurText: {
      required: false,
      type: String,
    },
  },

  mixins: [FullScreenPopupMixin],

  data() {
    return {
      ViewTypes,

      activeMediaIndex: 1,

      sendLeadPopupConfig: {
        isActive: false,
      },
      successModalConfig: {
        isActive: false,
      },

      successPopupText: 'Your request has been sent successfully.',
    };
  },

  async mounted() {
    // Users are fetched in store.fetchSingleProperty/fetchSinglePropertyById for property detail pages
    // No need to fetch here as it blocks the entire site
    
    if (this.property && this.property.property_profile_id) {
      const propertiesStore = usePropertiesStore();
      await propertiesStore.addPotentialViewing(this.property.property_profile_id);
    }
    
    this._fetchPropertiesForCurrentIdList();
  },

  computed: {
    singlePropertyRepoRef() {
      const data = getFromState('singlePropertyRepo') as SinglePropertyRepo;
      const route = useRoute();
      const identifier = route.params.property_id as string;

      if (data && data.length > 0) {
        return data.find((item) => item.identifier === identifier);
      }
      return null;
    },

    property() {
      const property = this.singlePropertyRepoRef as SingleProperty;
      if (property && property.data) {
        const item = property.data;
        return mapProperty({ info: item }, 0);
      }
      return null;
    },

    imageBlurFilter(): string | undefined {
      if (this.property && this.property?.is_locked) {
        return '14px';
      }
      return undefined;
    },

    propertyByIdRepoRef() {
      const identifier = (this as any).propertyIdentifierForMixin as string;
      if (!identifier) {
        return null;
      }
      const { data: repoData } = useDataFromState(RepoStates.SINGLE_PROPERTY_REPO);

      if (repoData && repoData.length > 0) {
        const singleRepo = repoData as SinglePropertyRepo;
        return singleRepo.find((item: SingleProperty) => item.identifier === identifier);
      }
      return null;
    },

    propertyById() {
      const propRef = this.propertyByIdRepoRef;
      if (propRef && propRef.data) {
        const item = propRef.data;
        return mapProperty({ info: item }, 0);
      }
      return null;
    },

    activeMedia() {
      if (this.property && this.property.medias) {
        return this.property.medias[this.activeMediaIndex];
      }
      return null;
    },

    _repositoryData() {
      const { data } = useDataFromState(RepoStates.SINGLE_PROPERTY_REPO);
      return data as SinglePropertyRepo | null;
    },

    propertiesFromIdList(): PropertyItemComponent[] {
      const ids = this.propertyIdList;
      const repo = this._repositoryData;

      if (!ids || ids.length === 0 || !repo) {
        return [];
      }

      const properties: PropertyItemComponent[] = [];
      ids.forEach(id => {
        const singleProperty = repo.find((item: SingleProperty) => item.identifier === id);
        if (singleProperty && singleProperty.data) {
          try {
            properties.push(mapProperty({ info: singleProperty.data }, properties.length));
          } catch(e) {
            console.error(`[PropertyMixin] Error mapping property ID ${id}:`, e);
          }
        }
      });
      return properties;
    },
    
    routes() {
      return ROUTES;
    },
  },

  watch: {
    propertyIdList: {
      handler: '_fetchPropertiesForCurrentIdList',
      deep: true,
      immediate: false,
    },
  },

  methods: {
    ...mapActions(usePropertiesStore, ['goBackToPropertyList']),

    async _fetchPropertiesForCurrentIdList() {
      const ids = this.propertyIdList;
      if (ids && ids.length > 0 && this.component) {
        // Users are fetched inside fetchSinglePropertyById (parallel with property fetch)
        const store = usePropertiesStore();
        try {
          await Promise.all(
            ids.map(id => store.fetchSinglePropertyById(this.component, id))
          );
        } catch (error) {
          console.error('[PropertyMixin] Error fetching properties for list:', error);
        }
      }
    },

    convert(value: number): string {
      return currencyFormat(value);
    },

    cutText(value: string): string {
      if (value) {
        return `${value.trim().slice(0, 120)}...`;
      }
      return '';
    },

    onClickedImage(image: { property_profile_media_id: number } | null) {
      if (this.property && this.property.medias && image?.property_profile_media_id && !this.property.is_locked) {
        if (image) {
          this.activeMediaIndex = this.property.medias.findIndex(
            (media) => media.property_profile_media_id === image.property_profile_media_id,
          );
        } else {
          this.activeMediaIndex = 2;
        }

        this.openJustLightBox('gallery');
      }
    },

    onSuccessModalClosed() {
      this.successModalConfig.isActive = false;
    },

    onSuccess() {
      this.sendLeadPopupConfig.isActive = false;
      this.successModalConfig.isActive = true;
    },

    onSendLead() {
      this.sendLeadPopupConfig.isActive = true;
    },
  },
});
